import React from 'react';
import { RCFormGroup } from '../RCFormGroup';
import type { FormattedString, TagType } from '../../types';

interface Props {
  /** ID of the input component, must be unique across the page */
  id: string;

  /** html attr, used by html form submission */
  name: string;

  /** Main label text (or more complex components if you really need to) */
  label: FormattedString;

  disabled?: boolean;

  /** Signals to browser's form validator that a non-empty value must be selected */
  required?: boolean;

  /** Validation error message */
  errorText?: string;
  /** Additional info displayed below label */
  helpText?: FormattedString;
  /** Annotation on the field, e.g. 'required', 'optional' */
  tag?: TagType;

  /** Hint to browser about the content of the field. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values */
  autoComplete?: string;

  /** called when value changes; updated value in evt.target.value */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  /** called when input element loses focus */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;

  /** called when a key on the keyboard is pressed */
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;

  /** Current value of the input */
  value: string;

  /**
   * This component only supports "text-like" inputs, other `<input>`s are rendered by different components.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types) for details on HTML input types
   */
  type?: 'text' | 'email' | 'number' | 'date'; // TODO: are these useful and similar enough to be variants? 'search' | 'url' |  'password' | 'tel'

  /**
   * Regex to validate input value against
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/pattern) for details
  */
  pattern?: string;

  /** Text that appears when the value is empty */
  placeholder?: string;

  /** Static text displayed inline before the input. Useful for currency symbols */
  prefixText?: string;

  /** Static text displayed inline after the input. Useful for "plus shipping", numerical units, or other context */
  suffixText?: string;

  /** Minimum character length for value */
  minLength?: number;

  /** Maximum character length for value */
  maxLength?: number;
}

export function RCTextInput({ type = 'text', ...props }: Props) {
  const isGrouped = !!(props.prefixText || props.suffixText);

  const inputEl = (
    <input
      autoComplete={props.autoComplete}
      disabled={props.disabled}
      id={props.id}
      name={props.name}
      onBlur={props.onBlur}
      onChange={props.onChange}
      placeholder={props.placeholder}
      required={props.required}
      type={type}
      pattern={props.pattern}
      value={props.value}
      className={isGrouped ? 'rc-text-input__input' : ''}
      onKeyPress={props.onKeyPress}
      minLength={props.minLength}
      maxLength={props.maxLength}
    />
  );

  return (
    <RCFormGroup
      errorText={props.errorText}
      helpText={props.helpText}
      inputId={props.id}
      label={props.label}
      tag={props.tag}
    >
      {isGrouped ? (
        <InputGroup
          prefixText={props.prefixText}
          suffixText={props.suffixText}
        >
          {inputEl}
        </InputGroup>
      ) : (
        inputEl
      )}
    </RCFormGroup>
  );
}

function InputGroup({ children, prefixText, suffixText }) {
  return (
    <div className="rc-text-input__group">
      {prefixText &&
        <div className="rc-text-input__text">{prefixText}</div>
      }
      {children}
      {suffixText &&
        <div className="rc-text-input__text">{suffixText}</div>
      }
    </div>
  );
}
